
import { useState } from 'react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';
import { Mail } from 'lucide-react';

export function EmailVerificationBanner() {
  const { user, setUser } = useAuthStore();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  if (!user || user.emailVerified) return null;

  const handleVerify = async () => {
    if (code.length !== 6) {
      setError('Enter the 6-digit code');
      return;
    }
    setError('');
    setLoading(true);

    const result = await api.verifyOtp(code);
    if (result.success) {
      setUser({ ...user, emailVerified: true });
      setSuccess('Email verified!');
    } else {
      setError(result.error?.message ?? 'Invalid code');
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    const result = await api.resendOtp();
    if (result.success) {
      setSuccess('New code sent to your email');
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error?.message ?? 'Failed to resend');
    }
    setResending(false);
  };

  if (success === 'Email verified!') {
    return (
      <div className="bg-green-50 border-b border-green-200 px-4 py-3">
        <p className="text-sm text-green-700 text-center font-medium">Email verified successfully!</p>
      </div>
    );
  }

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
      <div className="max-w-2xl mx-auto flex flex-col sm:flex-row items-center gap-3">
        <div className="flex items-center gap-2 text-amber-800">
          <Mail size={18} />
          <span className="text-sm font-medium">Verify your email to place orders</span>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, ''));
              setError('');
            }}
            placeholder="6-digit code"
            className="w-28 px-3 py-1.5 text-sm border border-amber-300 rounded-md bg-white focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500"
          />
          <button
            onClick={handleVerify}
            disabled={loading || code.length !== 6}
            className="px-3 py-1.5 text-sm font-medium bg-accent-500 text-white rounded-md hover:bg-accent-600 disabled:opacity-50"
          >
            {loading ? '...' : 'Verify'}
          </button>
          <button
            onClick={handleResend}
            disabled={resending}
            className="px-3 py-1.5 text-sm font-medium text-amber-700 hover:text-amber-900 underline"
          >
            {resending ? 'Sending...' : 'Resend'}
          </button>
        </div>

        {error && <span className="text-sm text-red-600">{error}</span>}
        {success && <span className="text-sm text-green-600">{success}</span>}
      </div>
    </div>
  );
}
